import { useRef, useState } from "react";
import {
  useParseProjectReceipt,
  useCommitProjectReceipt,
  useEstimateMaterials,
  type ParsedReceipt,
  type CommitReceiptItem,
  type EstimateMaterial,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Image, Loader2, Receipt, Upload, CheckCircle2 } from "lucide-react";

const ACCEPTED = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"].join(",");
const MAX_MB = 20;

type CostType = "labour" | "materials" | "fees" | "subcontractor" | "equipment" | "other";
const COST_TYPES: CostType[] = ["materials", "labour", "fees", "subcontractor", "equipment", "other"];
const costTypeLabel = (t: CostType) => (t === "labour" ? "labor" : t);

interface MappedItem {
  description: string;
  quantity: string;
  unitPrice: string;
  type: CostType;
  sourceMaterialId: number | "unmatched";
}

function autoMatch(
  description: string,
  materials: EstimateMaterial[],
): number | "unmatched" {
  if (!materials.length) return "unmatched";
  const lower = description.toLowerCase();
  const found = materials.find(
    (m) =>
      lower.includes(m.name.toLowerCase()) ||
      m.name.toLowerCase().includes(lower.split(" ")[0]),
  );
  return found ? found.id : "unmatched";
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

// ─── Upload screen ────────────────────────────────────────────────────────────
function UploadScreen({
  onParsed,
  projectId,
}: {
  onParsed: (receipt: ParsedReceipt, vendor: string, date: string) => void;
  projectId: number;
}) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const parse = useParseProjectReceipt(projectId);

  const handleFile = (file: File) => {
    if (!file) return;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_MB) {
      toast({ title: `File exceeds ${MAX_MB} MB`, variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target?.result as string;
      const comma = dataUrl.indexOf(",");
      const dataBase64 = dataUrl.slice(comma + 1);
      try {
        const result = await parse.mutateAsync({ dataBase64, mimeType: file.type });
        onParsed(result.receipt, result.receipt.vendor ?? "", result.receipt.receiptDate ?? "");
      } catch {
        toast({ title: "Failed to read receipt. Try a clearer photo.", variant: "destructive" });
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div
        onClick={() => !parse.isPending && inputRef.current?.click()}
        style={{
          border: "2px dashed hsl(var(--border))",
          borderRadius: "0.5rem",
          padding: "2rem",
          textAlign: "center",
          cursor: parse.isPending ? "default" : "pointer",
          opacity: parse.isPending ? 0.6 : 1,
          transition: "border-color 0.15s",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.5rem",
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) handleFile(file);
        }}
      >
        {parse.isPending ? (
          <>
            <Loader2 style={{ width: 32, height: 32, color: "hsl(var(--primary))", animation: "spin 1s linear infinite" }} />
            <p style={{ fontWeight: 500 }}>Reading receipt…</p>
            <p style={{ fontSize: "0.8rem", color: "hsl(var(--muted-foreground))" }}>Claude is extracting line items</p>
          </>
        ) : (
          <>
            <div style={{ display: "flex", gap: "0.5rem", color: "hsl(var(--muted-foreground))" }}>
              <Image style={{ width: 28, height: 28 }} />
              <Upload style={{ width: 28, height: 28 }} />
            </div>
            <p style={{ fontWeight: 500, marginTop: "0.25rem" }}>Drop receipt here or click to browse</p>
            <p style={{ fontSize: "0.8rem", color: "hsl(var(--muted-foreground))" }}>
              JPEG, PNG, or PDF · Phone photos work great · Up to {MAX_MB} MB
            </p>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// ─── Mapping screen ───────────────────────────────────────────────────────────
function MappingScreen({
  receipt,
  initialVendor,
  initialDate,
  materials,
  onCommit,
  onBack,
  projectId,
}: {
  receipt: ParsedReceipt;
  initialVendor: string;
  initialDate: string;
  materials: EstimateMaterial[];
  onCommit: () => void;
  onBack: () => void;
  projectId: number;
}) {
  const { toast } = useToast();
  const commit = useCommitProjectReceipt(projectId);

  const [mode, setMode] = useState<"line" | "single">("line");
  const [vendor, setVendor] = useState(initialVendor);
  const [date, setDate] = useState(initialDate);

  const [mapped, setMapped] = useState<MappedItem[]>(() =>
    receipt.items.map((item) => ({
      description: item.description,
      quantity: String(item.quantity),
      unitPrice: String(item.unitPrice),
      type: "materials" as CostType,
      sourceMaterialId: autoMatch(item.description, materials),
    })),
  );

  // Single-entry aggregate row
  const [singleMaterialId, setSingleMaterialId] = useState<number | "unmatched">("unmatched");
  const [singleType, setSingleType] = useState<CostType>("materials");
  const receiptTotal = receipt.total ?? receipt.subtotal ?? receipt.items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);

  const updateItem = (idx: number, patch: Partial<MappedItem>) =>
    setMapped((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const handleCommit = async () => {
    let items: CommitReceiptItem[];

    if (mode === "single") {
      items = [
        {
          name: vendor || "Receipt",
          vendor: vendor || null,
          date: date || null,
          quantity: 1,
          unitCost: receiptTotal,
          type: singleType,
          sourceMaterialId: singleMaterialId === "unmatched" ? null : singleMaterialId,
        },
      ];
    } else {
      items = mapped.map((it) => ({
        name: it.description || "Receipt item",
        vendor: vendor || null,
        date: date || null,
        quantity: parseFloat(it.quantity) || 1,
        unitCost: parseFloat(it.unitPrice) || 0,
        type: it.type,
        sourceMaterialId: it.sourceMaterialId === "unmatched" ? null : it.sourceMaterialId,
      }));
    }

    try {
      const result = await commit.mutateAsync({ items });
      toast({ title: `${result.created} cost${result.created !== 1 ? "s" : ""} added from receipt` });
      onCommit();
    } catch {
      toast({ title: "Failed to save costs. Please try again.", variant: "destructive" });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* Header info row */}
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 160px" }}>
          <Label style={{ fontSize: "0.8rem" }}>Vendor</Label>
          <Input
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder="e.g. Home Depot"
            style={{ marginTop: "0.25rem", height: 32, fontSize: "0.875rem" }}
          />
        </div>
        <div style={{ flex: "0 0 140px" }}>
          <Label style={{ fontSize: "0.8rem" }}>Receipt date</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ marginTop: "0.25rem", height: 32, fontSize: "0.875rem" }}
          />
        </div>
      </div>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <span style={{ fontSize: "0.8rem", color: "hsl(var(--muted-foreground))" }}>Import as:</span>
        <Button
          size="sm"
          variant={mode === "line" ? "default" : "outline"}
          onClick={() => setMode("line")}
          style={{ height: 28, fontSize: "0.8rem" }}
        >
          Line by line
        </Button>
        <Button
          size="sm"
          variant={mode === "single" ? "default" : "outline"}
          onClick={() => setMode("single")}
          style={{ height: 28, fontSize: "0.8rem" }}
        >
          Single total
        </Button>
      </div>

      {/* Mapping table */}
      {mode === "line" ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: "0.8rem", borderCollapse: "collapse", minWidth: 600 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid hsl(var(--border))", textAlign: "left" }}>
                <th style={{ padding: "0.375rem 0.5rem", fontWeight: 500 }}>Item</th>
                <th style={{ padding: "0.375rem 0.5rem", width: 64, textAlign: "right", fontWeight: 500 }}>Qty</th>
                <th style={{ padding: "0.375rem 0.5rem", width: 80, textAlign: "right", fontWeight: 500 }}>Unit cost</th>
                <th style={{ padding: "0.375rem 0.5rem", width: 100, fontWeight: 500 }}>Type</th>
                <th style={{ padding: "0.375rem 0.5rem", minWidth: 180, fontWeight: 500 }}>Maps to estimate material</th>
              </tr>
            </thead>
            <tbody>
              {mapped.map((item, i) => {
                const matched = item.sourceMaterialId !== "unmatched"
                  ? materials.find((m) => m.id === item.sourceMaterialId)
                  : null;
                return (
                  <tr key={i} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                    <td style={{ padding: "0.375rem 0.5rem" }}>
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(i, { description: e.target.value })}
                        style={{ height: 28, fontSize: "0.8rem" }}
                      />
                    </td>
                    <td style={{ padding: "0.375rem 0.5rem" }}>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(i, { quantity: e.target.value })}
                        style={{ height: 28, fontSize: "0.8rem", textAlign: "right" }}
                      />
                    </td>
                    <td style={{ padding: "0.375rem 0.5rem" }}>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => updateItem(i, { unitPrice: e.target.value })}
                        style={{ height: 28, fontSize: "0.8rem", textAlign: "right" }}
                      />
                    </td>
                    <td style={{ padding: "0.375rem 0.5rem" }}>
                      <Select
                        value={item.type}
                        onValueChange={(v) => updateItem(i, { type: v as CostType })}
                      >
                        <SelectTrigger style={{ height: 28, fontSize: "0.8rem" }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COST_TYPES.map((t) => (
                            <SelectItem key={t} value={t} style={{ fontSize: "0.8rem", textTransform: "capitalize" }}>
                              {costTypeLabel(t)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td style={{ padding: "0.375rem 0.5rem" }}>
                      <Select
                        value={String(item.sourceMaterialId)}
                        onValueChange={(v) =>
                          updateItem(i, { sourceMaterialId: v === "unmatched" ? "unmatched" : parseInt(v, 10) })
                        }
                      >
                        <SelectTrigger style={{ height: 28, fontSize: "0.8rem" }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unmatched" style={{ fontSize: "0.8rem" }}>
                            Unmatched cost
                          </SelectItem>
                          {materials.map((m) => (
                            <SelectItem key={m.id} value={String(m.id)} style={{ fontSize: "0.8rem" }}>
                              {m.name}
                              {m.lineItemDescription ? ` (${m.lineItemDescription})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {matched && (
                        <p style={{ fontSize: "0.7rem", color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                          Quoted: {formatCurrency(matched.unitCost)}/{matched.unit ?? "unit"}
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        // Single-entry mode
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <Label style={{ fontSize: "0.8rem" }}>Total amount</Label>
            <Input
              type="number"
              value={receiptTotal}
              readOnly
              style={{ marginTop: "0.25rem", height: 32, fontSize: "0.875rem", width: 120 }}
            />
          </div>
          <div>
            <Label style={{ fontSize: "0.8rem" }}>Type</Label>
            <Select value={singleType} onValueChange={(v) => setSingleType(v as CostType)}>
              <SelectTrigger style={{ marginTop: "0.25rem", height: 32, fontSize: "0.875rem", width: 140 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COST_TYPES.map((t) => (
                  <SelectItem key={t} value={t} style={{ textTransform: "capitalize" }}>
                    {costTypeLabel(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <Label style={{ fontSize: "0.8rem" }}>Maps to estimate material</Label>
            <Select
              value={String(singleMaterialId)}
              onValueChange={(v) =>
                setSingleMaterialId(v === "unmatched" ? "unmatched" : parseInt(v, 10))
              }
            >
              <SelectTrigger style={{ marginTop: "0.25rem", height: 32, fontSize: "0.875rem" }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unmatched">Unmatched cost</SelectItem>
                {materials.map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {m.name}
                    {m.lineItemDescription ? ` (${m.lineItemDescription})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {singleMaterialId !== "unmatched" && (() => {
              const m = materials.find((mat) => mat.id === singleMaterialId);
              return m ? (
                <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                  Quoted: {formatCurrency(m.unitCost)}/{m.unit ?? "unit"}
                </p>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "0.25rem" }}>
        <Button variant="outline" size="sm" onClick={onBack} disabled={commit.isPending}>
          ← Back
        </Button>
        <Button size="sm" onClick={handleCommit} disabled={commit.isPending}>
          {commit.isPending ? (
            <Loader2 style={{ width: 14, height: 14, marginRight: 6, animation: "spin 1s linear infinite" }} />
          ) : null}
          Add {mode === "line" ? `${mapped.length} cost${mapped.length !== 1 ? "s" : ""}` : "cost"} to project
        </Button>
      </div>
    </div>
  );
}

// ─── Success screen ────────────────────────────────────────────────────────────
function SuccessScreen({ onAnother }: { onAnother: () => void }) {
  return (
    <div style={{ textAlign: "center", padding: "1rem 0", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
      <CheckCircle2 style={{ width: 36, height: 36, color: "hsl(var(--primary))" }} />
      <p style={{ fontWeight: 500 }}>Costs added to project</p>
      <p style={{ fontSize: "0.85rem", color: "hsl(var(--muted-foreground))" }}>
        They're now in the Costs tab and will appear in your quoted vs actual metrics.
      </p>
      <Button size="sm" variant="outline" onClick={onAnother}>
        <Receipt style={{ width: 14, height: 14, marginRight: 6 }} />
        Import another receipt
      </Button>
    </div>
  );
}

// ─── Main export ───────────────────────────────────────────────────────────────
type Screen = "upload" | "map" | "done";

export function ReceiptImport({ projectId }: { projectId: number }) {
  const { data: materials = [] } = useEstimateMaterials(projectId);
  const [screen, setScreen] = useState<Screen>("upload");
  const [receipt, setReceipt] = useState<ParsedReceipt | null>(null);
  const [vendor, setVendor] = useState("");
  const [date, setDate] = useState("");

  if (screen === "done") {
    return <SuccessScreen onAnother={() => { setReceipt(null); setScreen("upload"); }} />;
  }

  if (screen === "map" && receipt) {
    return (
      <MappingScreen
        receipt={receipt}
        initialVendor={vendor}
        initialDate={date}
        materials={materials}
        projectId={projectId}
        onCommit={() => setScreen("done")}
        onBack={() => setScreen("upload")}
      />
    );
  }

  return (
    <UploadScreen
      projectId={projectId}
      onParsed={(r, v, d) => {
        setReceipt(r);
        setVendor(v);
        setDate(d);
        setScreen("map");
      }}
    />
  );
}
